import { Button, Group, Paper, Stack, Text, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconTrash } from '@tabler/icons-react'
import { useState } from 'react'

import { DeleteChannelFinalConfirmationModal } from '@/components/DeleteChannelFinalConfirmationModal'
import { DeleteChannelModal } from '@/components/DeleteChannelModal'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { navigateTo } from '@/utils/navigation'

export function DeleteChannelCard({ channelName, disabled }: { channelName: string; disabled?: boolean }) {
  const { api, authenticationStore } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [showDeleteChannelModal, setShowDeleteChannelModal] = useState(false)
  const [showFinalDeleteConfirmModal, setShowFinalDeleteConfirmModal] = useState(false)
  const [isDeletingChannel, setIsDeletingChannel] = useState(false)

  const handleFirstDeleteConfirm = () => {
    setShowDeleteChannelModal(false)
    setShowFinalDeleteConfirmModal(true)
  }

  // On success, log out and land on login with a success banner.
  const handleDeleteChannel = async () => {
    const sponsorId = authenticationStore.user?.sponsorId
    if (!sponsorId) {
      notifications.show({
        title: 'Could Not Delete Channel',
        message: 'Missing channel context. Please refresh the page and try again.',
        color: 'red',
      })
      return
    }

    setIsDeletingChannel(true)
    try {
      await api.deleteSponsor(sponsorId)
    } catch (error) {
      console.error('Failed to delete channel:', error)
      notifications.show({
        title: 'Could Not Delete Channel',
        message:
          error instanceof Error && error.message
            ? error.message
            : 'Something went wrong while deleting your channel. Please try again.',
        color: 'red',
      })
      setIsDeletingChannel(false)
      return
    }

    try {
      await authenticationStore.logOut()
    } catch (error) {
      console.warn('Logout after channel deletion failed; clearing session anyway.', error)
    }

    setIsDeletingChannel(false)
    setShowFinalDeleteConfirmModal(false)

    navigateTo('login', {
      searchParams: {
        channelDeleted: '1',
        ...(channelName ? { channelName } : {}),
      },
    })
  }

  return (
    <>
      <Paper
        component="section"
        aria-label="Delete this Channel"
        radius="md"
        p={isMobile ? 'md' : 'lg'}
        withBorder
        style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Stack gap="md">
          <Title order={isMobile ? 6 : 5} c="red.7">
            Delete this Channel
          </Title>
          <Group justify="space-between" align="flex-start" gap="md" wrap={isMobile ? 'wrap' : 'nowrap'}>
            <Text size="sm" c="dimmed" style={{ flex: 1, minWidth: 0 }}>
              Permanently deactivate this channel and all of its content. End users will lose access immediately. This
              action cannot be undone.
            </Text>
            <Button
              color="red"
              variant="outline"
              leftSection={<IconTrash size={16} />}
              onClick={() => setShowDeleteChannelModal(true)}
              disabled={disabled || isDeletingChannel || !channelName}>
              Delete Channel
            </Button>
          </Group>
        </Stack>
      </Paper>

      <DeleteChannelModal
        opened={showDeleteChannelModal}
        onClose={() => setShowDeleteChannelModal(false)}
        onConfirm={handleFirstDeleteConfirm}
        channelName={channelName}
      />

      <DeleteChannelFinalConfirmationModal
        opened={showFinalDeleteConfirmModal}
        onClose={() => setShowFinalDeleteConfirmModal(false)}
        onConfirm={handleDeleteChannel}
        channelName={channelName}
        isLoading={isDeletingChannel}
      />
    </>
  )
}
